import { useLazyQuery } from '@apollo/client';
import { Query, QueryGetTenantNameArgs } from 'graphql/generated/graphqlTypes';
import { GET_TENANT_NAME } from 'graphql/queries';
import { useCallback, useEffect, useState } from 'react';
import { BaseHookProps } from '../shared/types';

interface Props extends BaseHookProps {
  azureTenantId: string | null;
}

const useTenantName = ({ handleFetchError, azureTenantId }: Props) => {
  const NUMBER_OF_RETRY_BEFORE_ERROR = 60;
  const TIME_TO_NEXT_RETRY = 3000;
  const [retryCount, setRetryCount] = useState(0);
  const [tenantName, setTenantName] = useState<string>();
  const [getTenantNameQuery, { loading, error, data }] = useLazyQuery<
    Pick<Query, 'getTenantName'>,
    QueryGetTenantNameArgs
  >(GET_TENANT_NAME);

  useEffect(() => {
    if (data) setTenantName(data.getTenantName);
  }, [data]);

  const handleRetry = useCallback(() => {
    if (azureTenantId && retryCount !== 0) {
      setRetryCount((current) => current + 1);
      getTenantNameQuery({
        variables: {
          azureTenantId
        }
      });
    }
  }, [azureTenantId, getTenantNameQuery, retryCount]);

  useEffect(() => {
    // pulling name from entra - when permission is granted name is available
    if (azureTenantId) {
      setRetryCount(1);
      getTenantNameQuery({
        variables: {
          azureTenantId
        }
      });
    }
  }, [azureTenantId, getTenantNameQuery]);

  useEffect(() => {
    if (!error) return undefined;
    if (retryCount < NUMBER_OF_RETRY_BEFORE_ERROR) {
      const timeoutId = setTimeout(handleRetry, TIME_TO_NEXT_RETRY);
      return () => clearTimeout(timeoutId);
    }
    if (retryCount === NUMBER_OF_RETRY_BEFORE_ERROR) handleFetchError('Error while fetching tenant name');
    return undefined;
  }, [error, retryCount, handleRetry, handleFetchError]);

  const isLoading = retryCount !== 0 && tenantName === undefined && loading;
  return {
    tenantName,
    loading: isLoading
  };
};

export default useTenantName;
